import { JsonEditorComponentModule } from '@b2b/common/components/json-editor/json-editor.component';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ThemesData } from '@b2b/common/resolvers';
import { TicketConfigData } from '@b2b/common/resolvers/ticket-config-data.resolver';
import { ApiService } from '@b2b/common/services/api/types';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ACMEResponse } from '@b2b/types/core';
import { TODO } from '@b2b/types/core/utils';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IScope, ITimeoutService } from 'angular';

ThemesController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  'ThemesData',
  'TicketConfigData',
  'ApiService',
  'PermissionsService',
  'NoticeFactory',
];

function ThemesController(
  $scope: IScope & { themes: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  ThemesData: { data: ACMEResponse<ThemesData> },
  TicketConfigData: { data: ACMEResponse<TicketConfigData> },
  ApiService: ApiService,
  PermissionsService: PermissionsService,
  NoticeFactory: NoticeFactory
) {
  const ctrl = {
    model: {
      variables: '{}',
      emailVariables: '{}',
      textVariables: '{}',
      links: '[]',
      pointOfSaleConfig: '{}',
      ticketConfig: '{}',
      membershipConfig: {
        membershipCardLayouts: {
          cssText: '',
          htmlText: '',
        },
      },
      customScripts: '',
    } as TODO,
    cache: {} as TODO,
    notice: new NoticeFactory(),
    saveType: '',
    state: 'web',
    tabErrors: {} as Record<string, boolean>,

    init: function () {
      if (ThemesData.data && ThemesData.data.success) {
        ctrl.success.init(ThemesData);
      } else {
        ctrl.error.init(ThemesData);
      }

      if (TicketConfigData.data && TicketConfigData.data.success) {
        ctrl.success.getTicketConfig(TicketConfigData);
      } else {
        ctrl.error.getTicketConfig(TicketConfigData);
      }

      ctrl.setState();
    },

    isState: function (state) {
      return ctrl.state === state;
    },

    hasTabErrors: function (tab) {
      return ctrl.tabErrors[tab];
    },

    setState: function () {
      const state = $state.current;
      const params = $state.params;

      switch (state.name) {
        case 'application.themes.web':
          ctrl.state = 'web';
          break;
        case 'application.themes.config.donation':
          ctrl.state = 'donation';
          break;
        default:
          ctrl.state = params.detail;
          break;
      }

      ctrl.updateState();
    },

    getState: function (name) {
      ctrl.state = name;
      ctrl.updateState();
      switch (name) {
        case 'scripts':
        case 'membership':
        case 'tickets':
        case 'pos':
        case 'email':
          $state.go('application.themes.detail', { detail: name });
          break;
        case 'donation':
          $state.go('application.themes.config.donation');
          break;
        case 'web':
          $state.go('application.themes.web');
          break;
      }
    },

    setSaveType: function (val) {
      ctrl.saveType = val;
    },

    updateState: function () {
      let buttonRightLabel = 'Update';
      const buttonData: TODO = {
        title: 'Themes',
      };

      ctrl.setSaveType(ctrl.state);

      switch (ctrl.state) {
        case 'pos':
          buttonRightLabel = 'Update Point of Sale';
          break;
        case 'email':
          buttonRightLabel = 'Update Email';
          break;
        case 'tickets':
          buttonRightLabel = 'Update Tickets';
          break;
        case 'membership':
          buttonRightLabel = 'Update Membership';
          break;
        case 'scripts':
          buttonRightLabel = 'Update Scripts';
          break;
        default:
          ctrl.setSaveType('all');
      }

      if (ctrl.state !== 'donation') {
        buttonData.buttonRight = {
          permissions: 'WL_THEME_CONFIG_UPDATE',
          label: buttonRightLabel,
          action: function () {
            ctrl.complete();
          },
        };

        buttonData.buttonLeft = {
          label: 'Cancel',
          action: function () {
            ctrl.reset();
          },
        };
      }

      $scope.$emit('Application::contentTitleChange', buttonData);
    },

    reset: function () {
      ctrl.model = angular.copy(ctrl.cache);
      ctrl.notice.hideErrorMessages();
      ctrl.notice.hideSuccessMessages();
      ctrl.process();
    },

    process: function () {
      let canProcess = true;
      const data = {
        variables: '{}',
        emailVariables: '{}',
        textVariables: '{}',
        links: '[]',
        pointOfSaleConfig: '{}',
        ticketConfig: '{}',
        membershipConfig: {
          membershipCardLayouts: {
            cssText: '',
            htmlText: '',
          },
        },
        customScripts: '',
      };

      if (ctrl.model.variables) {
        data.variables = ctrl.model.variables.replace(/[\r\n]+[,\s]+/g, '');
      }
      if (ctrl.model.textVariables) {
        data.textVariables = ctrl.model.textVariables.replace(/[\r\n]+[,\s]+/g, '');
      }
      if (ctrl.model.emailVariables) {
        data.emailVariables = ctrl.model.emailVariables.replace(/[\r\n]+[,\s]+/g, '');
      }
      if (ctrl.model.links) {
        data.links = ctrl.model.links.replace(/[\r\n]+[,\s]+/g, '');
      }

      if (ctrl.model.pointOfSaleConfig) {
        data.pointOfSaleConfig = ctrl.model.pointOfSaleConfig.replace(/[\r\n]+[,\s]+/g, '');
      }

      if (ctrl.model.ticketConfig) {
        data.ticketConfig = ctrl.model.ticketConfig.replace(/[\r\n]+[,\s]+/g, '');
      }

      if (ctrl.model.membershipConfig) {
        if (ctrl.model.membershipConfig.membershipCardLayouts) {
          if (ctrl.model.membershipConfig.membershipCardLayouts.cssText) {
            data.membershipConfig.membershipCardLayouts.cssText =
              ctrl.model.membershipConfig.membershipCardLayouts.cssText;
          }
          if (ctrl.model.membershipConfig.membershipCardLayouts.htmlText) {
            data.membershipConfig.membershipCardLayouts.htmlText =
              ctrl.model.membershipConfig.membershipCardLayouts.htmlText;
          }
        }
      }

      if (ctrl.model.customScripts) {
        data.customScripts = ctrl.model.customScripts;
      }

      ctrl.tabErrors.web = false;
      ctrl.tabErrors.email = false;
      ctrl.tabErrors.pos = false;
      ctrl.tabErrors.tickets = false;
      ctrl.tabErrors.membership = false;
      ctrl.tabErrors.scripts = false;

      if (validateScripts(ctrl.model.customScripts)) {
        data.customScripts = ctrl.model.customScripts;
        ctrl.tabErrors.scripts = false;
      } else {
        ctrl.tabErrors.scripts = true;
      }

      try {
        data.variables = JSON.parse(data.variables);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.web = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in CSS Variables' }]);
      }
      try {
        data.textVariables = JSON.parse(data.textVariables);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.web = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in Text Variables' }]);
      }

      try {
        data.links = JSON.parse(data.links);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.web = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in Links' }]);
      }

      try {
        data.emailVariables = JSON.parse(data.emailVariables);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.email = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in Email Variables' }]);
      }

      try {
        data.pointOfSaleConfig = JSON.parse(data.pointOfSaleConfig);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.pos = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in Point of Sale' }]);
      }

      try {
        data.ticketConfig = JSON.parse(data.ticketConfig);
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.tickets = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid JSON in Ticket Config' }]);
      }

      try {
        // TODO: Placeholder for consistency in case we add JSON string configs in the future
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.membership = true;
        ctrl.notice.showErrorMessages([{ message: 'Invalid Membership Config' }]);
      }

      try {
        if (!validateScripts(data.customScripts)) {
          canProcess = false;
          ctrl.tabErrors.scripts = true;
          ctrl.notice.showErrorMessages([
            { message: 'Dangerous javascript encountered. eval, document.write, and script tags are not allowed.' },
          ]);
        }
      } catch (e) {
        canProcess = false;
        ctrl.tabErrors.scripts = true;
        ctrl.notice.showErrorMessages([
          { message: 'Dangerous javascript encountered. eval, document.write, and script tags are not allowed.' },
        ]);
      }

      return canProcess ? data : canProcess;
    },

    complete: function () {
      ctrl.notice.hideSuccessMessages();
      ctrl.notice.hideErrorMessages();

      const processed = ctrl.process();

      if (processed !== false) {
        if (ctrl.saveType === 'email' && PermissionsService.check('WL_THEME_CONFIG_UPDATE')) {
          ApiService.themes
            .updateEmail(processed)
            .then((response) => ctrl.success.complete(response))
            .catch((response) => ctrl.error.complete(response));
        } else if (ctrl.saveType === 'tickets' && PermissionsService.check('WL_TICKET_CONFIG_UPDATE')) {
          ApiService.themes
            .updateTicketConfig(processed)
            .then((response) => ctrl.success.complete(response))
            .catch((response) => ctrl.error.complete(response));
        } else if (
          PermissionsService.check([
            'WL_THEME_CONFIG_UPDATE',
            'WL_POS_CONFIG_UPDATE',
            'WL_TICKET_CONFIG_UPDATE',
            'WL_MEMBERSHIP_CONFIG_UPDATE',
            'WL_CUSTOM_SCRIPTS_UPDATE',
          ])
        ) {
          ApiService.themes
            .update(processed)
            .then((response) => ctrl.success.complete(response))
            .catch((response) => ctrl.error.complete(response));
        } else {
          $scope.$emit('Permissions::error');
        }
      }
    },

    success: {
      init: function (response: { data: ACMEResponse<ThemesData> }) {
        ctrl.model.variables = JSON.stringify(response.data.payload.variables ?? {}, undefined, 2);
        ctrl.model.textVariables = JSON.stringify(response.data.payload.textVariables ?? {}, undefined, 2);
        ctrl.model.emailVariables = JSON.stringify(response.data.payload.emailVariables ?? {}, undefined, 2);
        ctrl.model.links = JSON.stringify(response.data.payload.links ?? [], undefined, 2);
        ctrl.model.customScripts = response.data.payload.customScripts;

        if (response.data.payload.membershipConfig) {
          ctrl.model.membershipConfig = response.data.payload.membershipConfig;
        } else {
          ctrl.model.membershipConfig = {
            membershipCardLayouts: {
              cssText: '',
              htmlText: '',
            },
          };
        }

        ctrl.cache = angular.copy(ctrl.model);
      },

      complete: function (response) {
        ctrl.notice.showSuccessMessages([{ message: 'Successfully Updated!' }], { duration: 3000 });
        ctrl.cache = angular.copy(ctrl.model);
      },
      getTicketConfig: function (response) {
        ctrl.model.ticketConfig = JSON.stringify(response.data.payload.ticketConfig ?? {}, undefined, 2);
        ctrl.cache = angular.copy(ctrl.model);
      },
    },

    error: {
      init: function (response) {},
      complete: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },
      getTicketConfig: function (response) {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },
    },
  };

  $scope.themes = ctrl;
  ctrl.init();
}

export const ThemesControllerModule = angular
  .module('acme.view.application.themes.controllers', [
    uiRouter,
    'acme.common.service.api',
    'acme.common.service.permissions',
    'acme.common.factory.notice',
    JsonEditorComponentModule,
  ])
  .controller('ThemesController', ThemesController).name;

export function validateScripts(scripts: unknown): boolean {
  const regexes = [/<\/?script.*?>/gi, /document\.write\(/gi, /eval\(/gi];

  if (typeof scripts !== 'string') {
    return false;
  }

  for (const regex of regexes) {
    if (regex.test(scripts)) {
      return false;
    }
  }

  return true;
}
